import { ResourceClient } from '../client/types';
import { AnyResourceDefinition } from '../resources/types';
import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import {
  BaseDescriptor,
  usePulseClient,
  BaseDescriptorWithKey,
} from './shared';
import { processStack } from '../devtools/processStack';

export type BaseResourceDescriptor = {
  resource: AnyResourceDefinition;
  params: unknown;
  instanceKey: string;
  skip: boolean;
  pollInterval?: number;
  pollGroup?: string;
};

const useProcessDescriptors = <
  TDescriptor extends BaseDescriptor<any, any, any>,
>(
  descriptors: TDescriptor[]
): [string, (TDescriptor & BaseDescriptorWithKey<any, any, any>)[]] =>
  useMemo(() => {
    const processed: (TDescriptor & BaseDescriptorWithKey<any, any, any>)[] =
      [];

    let id = '';

    for (const descriptor of descriptors) {
      const instanceKey = descriptor.resource.identifyInstance(
        descriptor.params
      );

      id += `${instanceKey}-${descriptor.skip ?? 'false'}-${
        descriptor.pollInterval ?? '0'
      }-${descriptor.pollGroup ?? ''}-${descriptor.passive ?? 'false'}-${
        descriptor.keepAliveWhenHidden ?? 'false'
      }:::`;

      processed.push({
        ...descriptor,
        instanceKey,
      });
    }

    return [id, processed];
  }, [descriptors]);

export const useResourceObserver = <
  TDescriptor extends BaseDescriptor<any, any, any>,
  TSnapshot,
>(
  descriptors: TDescriptor[],
  resolveSnapshot: (
    client: ResourceClient,
    descriptor: TDescriptor & BaseDescriptorWithKey<any, any, any>
  ) => TSnapshot
): TSnapshot[] => {
  const client = usePulseClient();

  const [queryId, withKeys] = useProcessDescriptors(descriptors);

  // getSnapshot is memoized on queryId, but descriptors may carry fields (like select)
  // that change without affecting queryId. Refs ensure getSnapshot always reads fresh values.
  const withKeysRef = useRef(withKeys);
  // eslint-disable-next-line react-hooks/refs
  withKeysRef.current = withKeys;

  const resolveRef = useRef(resolveSnapshot);
  // eslint-disable-next-line react-hooks/refs
  resolveRef.current = resolveSnapshot;

  const stackRef = useRef<string | null>(null);
  // eslint-disable-next-line react-hooks/refs
  if (stackRef.current === null && client.devtoolsId) {
    stackRef.current = processStack(new Error()) ?? '';
  }

  const subscribe = useCallback(
    (callback: () => void) => {
      const unsubs: (() => void)[] = [];
      const attribution = {
        kind: 'external' as const,
        stack: stackRef.current ?? undefined,
      };

      for (const descriptor of withKeys) {
        if (descriptor.skip) {
          continue;
        }

        unsubs.push(client.watch(descriptor, callback, attribution));
      }

      return () => {
        for (const unsub of unsubs) {
          unsub();
        }
      };
    },
    // queryId is a proxy for our subscription set changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryId]
  );

  // eslint-disable-next-line react-hooks/immutability
  const getSnapshot = useMemo(() => {
    let cached: TSnapshot[] = [];

    return (): TSnapshot[] => {
      const keys = withKeysRef.current;
      const resolve = resolveRef.current;
      const next: TSnapshot[] = [];
      let hasChanged = keys.length !== cached.length;

      for (let i = 0; i < keys.length; i++) {
        const snapshot = resolve(client, keys[i]);
        next.push(snapshot);

        if (snapshot !== cached[i]) {
          hasChanged = true;
        }
      }

      if (!hasChanged) {
        return cached;
      }

      // eslint-disable-next-line react-hooks/immutability
      cached = next;
      return cached;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
